import type { FloorPlanRoomOption } from '../models/floorplan-room';

/**
 * Which rooms to offer for a desk on the storey being edited.
 *
 * An office's rooms are not per-floor — BDO Utrecht has 25 of them across six storeys, of
 * which any given floor uses three to six — so offering all of them means scanning a list to
 * find the one room that could plausibly be right. Grouping by floor turns that back into
 * picking.
 *
 * Nothing is ever removed, only sorted into groups. Two of them are not optional:
 *
 * - `unplaced` — rooms whose desks appear on no plan at all. Six live rooms are in that
 *   state, every one of them with workplaces. Hiding them would mean a room's first desk
 *   could never be placed, which is precisely when an admin needs the room most.
 * - `otherFloors` — offered behind a toggle. No stored room spans two floors, so this is
 *   normally empty of anything useful; it exists so a mislinked desk can still be corrected
 *   rather than being invisible from every floor.
 */
export interface GroupedRooms {
  readonly thisFloor: FloorPlanRoomOption[];
  readonly unplaced: FloorPlanRoomOption[];
  readonly otherFloors: FloorPlanRoomOption[];
}

interface Options {
  readonly rooms: readonly FloorPlanRoomOption[];
  readonly storey: number;
  /**
   * Rooms used by desks in the open document. A desk placed a moment ago has not reached the
   * database, so the server still reports its room as belonging to another floor or to none —
   * without this, the room an admin just chose would drop out of the list on the next desk.
   */
  readonly roomIdsOnThisFloor?: ReadonlySet<string>;
  /**
   * The room the selection currently sits in. Always offered, whichever floor it is on: a
   * dropdown whose own value is missing renders blank, and the next click on it silently
   * reassigns the desk.
   */
  readonly currentRoomId?: string | null;
}

export const groupRoomsByFloor = ({
  rooms,
  storey,
  roomIdsOnThisFloor,
  currentRoomId,
}: Options): GroupedRooms => {
  const thisFloor: FloorPlanRoomOption[] = [];
  const unplaced: FloorPlanRoomOption[] = [];
  const otherFloors: FloorPlanRoomOption[] = [];

  rooms.forEach((room) => {
    if (room.storey === storey || roomIdsOnThisFloor?.has(room.id)) {
      thisFloor.push(room);
      return;
    }

    if (room.storey === null) {
      unplaced.push(room);
      return;
    }

    if (room.id === currentRoomId) {
      // Kept in the main group rather than the hidden one, so the control can always render
      // its own value without the admin first having to reveal the other floors.
      thisFloor.push(room);
      return;
    }

    otherFloors.push(room);
  });

  return { thisFloor, unplaced, otherFloors };
};

/** The rooms of the desks currently drawn on this storey, including unsaved ones. */
export const roomIdsInUse = (desks: ReadonlyArray<{ readonly roomId: string | null }>): Set<string> => new Set(
  desks.map((desk) => desk.roomId).filter((roomId): roomId is string => roomId !== null),
);

export default groupRoomsByFloor;
